import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "@/store"
import { AlarmNotificationsState } from "@/store/alarm-notifications/types"
import {
    AlarmNotificationChange,
    AlarmNotificationDelivery,
    AlarmNotificationEffective,
    AlarmNotificationFilter,
    AlarmNotificationNode,
    AlarmNotificationPage,
    AlarmNotificationScope,
    AlarmNotificationScopes,
    AlarmNotificationTarget,
    AlarmNotificationTreeData,
    ROOT_KEY,
    TREE_PAGE_SIZE,
    CHILDREN_PAGE_SIZE,
    nodeKey,
    nodeState,
    scopeIdField,
} from "@/types/alarm-notification"

const ENDPOINT = "/auth/user/alarm-subscriptions"
const MAX_LIMIT = 200

const fetchFocusName = async (
    scope: AlarmNotificationScope,
    id: string,
    rootGetters: Record<string, any>,
): Promise<string> => {
    if (scope === AlarmNotificationScopes.LOCATION) {
        const res = await axios.get(`/locations/${id}`)

        return res.data.data.location.name
    }

    if (scope === AlarmNotificationScopes.DEVICE) {
        const res = await axios.get(`/devices/${id}`)

        return res.data.data.device.name
    }

    return rootGetters["breadcrumbs/getCategorizedData"]("location-group", id)?.title ?? ""
}

const matchedCount = (keys: string[], nodes: Record<string, AlarmNotificationNode>): number =>
    keys.filter((key) => nodes[key]?.match).length

const pageParams = (loaded: number, append: boolean): Record<string, number> => ({
    offset: append ? loaded : 0,
    limit: append ? TREE_PAGE_SIZE : Math.min(Math.max(loaded, TREE_PAGE_SIZE), MAX_LIMIT),
})

const emptyFilter = (): AlarmNotificationFilter => ({ search: "", onlySet: false, scope: null })

const isFilterActive = ({ search, onlySet, scope }: AlarmNotificationFilter): boolean =>
    !!search.trim() || onlySet || !!scope

const fetchTree = async (
    companyId: number | null,
    params: Record<string, string | number>,
): Promise<{ nodes: AlarmNotificationNode[]; total: number }> => {
    const res = await axios.get<AlarmNotificationTreeData>(`${ENDPOINT}/tree`, {
        params: companyId ? { ...params, companyId } : params,
    })

    return { nodes: res.data.data.nodes, total: res.data.meta.pagination.total }
}

const state: AlarmNotificationsState = {
    nodes: {},
    children: {},
    expandedKeys: [],
    filter: emptyFilter(),
    filtered: null,
    focusKey: null,
    companyId: null,
    loadingMore: false,
    loading: false,
    loadingKeys: [],
    savingKeys: [],
    delivery: null,
    savingDelivery: false,
    effective: null,
}

const mutations: MutationTree<AlarmNotificationsState> = {
    upsertNodes(state, nodes: AlarmNotificationNode[]) {
        state.nodes = {
            ...state.nodes,
            ...Object.fromEntries(nodes.map((node) => [node.key, node])),
        }
    },
    setChildren(
        state,
        { key, page, append }: { key: string; page: AlarmNotificationPage; append: boolean },
    ) {
        const keys = append ? [...(state.children[key]?.keys ?? []), ...page.keys] : page.keys

        state.children = { ...state.children, [key]: { keys, total: page.total } }
    },
    setExpandedKeys(state, keys: string[]) {
        state.expandedKeys = keys
    },
    setFilter(state, filter: AlarmNotificationFilter) {
        state.filter = filter
    },
    setFiltered(state, filtered: AlarmNotificationPage | null) {
        state.filtered = filtered
    },
    setFocusKey(state, key: string | null) {
        state.focusKey = key
    },
    setCompanyId(state, companyId: number | null) {
        state.companyId = companyId
    },
    setLoadingMore(state, loadingMore: boolean) {
        state.loadingMore = loadingMore
    },
    setLoading(state, loading: boolean) {
        state.loading = loading
    },
    addLoadingKey(state, key: string) {
        state.loadingKeys = [...state.loadingKeys, key]
    },
    removeLoadingKey(state, key: string) {
        state.loadingKeys = state.loadingKeys.filter((k) => k !== key)
    },
    addSavingKey(state, key: string) {
        state.savingKeys = [...state.savingKeys, key]
    },
    removeSavingKey(state, key: string) {
        state.savingKeys = state.savingKeys.filter((k) => k !== key)
    },
    setDelivery(state, delivery: AlarmNotificationDelivery | null) {
        state.delivery = delivery
    },
    setSavingDelivery(state, saving: boolean) {
        state.savingDelivery = saving
    },
    setEffective(state, effective: AlarmNotificationEffective | null) {
        state.effective = effective
    },
    reset(state) {
        state.nodes = {}
        state.children = {}
        state.expandedKeys = []
        state.filter = emptyFilter()
        state.filtered = null
        state.focusKey = null
    },
}

const actions: ActionTree<AlarmNotificationsState, RootState> = {
    async init(
        { commit, dispatch },
        { focusKey, companyId }: { focusKey: string | null; companyId: number | null },
    ) {
        commit("reset")
        commit("setFocusKey", focusKey)
        commit("setCompanyId", companyId)
        await dispatch("loadPage")
    },
    async loadPage({ commit, dispatch, state }) {
        commit("setLoading", true)

        try {
            if (state.focusKey) {
                await dispatch("fetchFocus")
            } else if (isFilterActive(state.filter)) {
                await dispatch("fetchFiltered")
            } else {
                await dispatch("fetchRoots")
            }
        } catch (e) {
            console.error("Error while fetching alarm notifications", e)
            dispatch(
                "toastMessageModule/showMessage",
                { message: "alarm_notifications.load_error", color: "red", isTranslation: true },
                { root: true },
            )
        } finally {
            commit("setLoading", false)
        }
    },
    async fetchRoots({ commit, state }, append = false) {
        const loaded = state.children[ROOT_KEY]?.keys.length ?? 0
        const { nodes, total } = await fetchTree(state.companyId, pageParams(loaded, append))

        commit("upsertNodes", nodes)
        commit("setChildren", {
            key: ROOT_KEY,
            page: { keys: nodes.map((node) => node.key), total },
            append,
        })
    },
    async fetchFiltered({ commit, state }, append = false) {
        const { search, onlySet, scope } = state.filter
        const previous = state.filtered?.keys ?? []
        const params: Record<string, string | number> = pageParams(
            matchedCount(previous, state.nodes),
            append,
        )

        if (search.trim()) params.search = search.trim()
        if (onlySet) params.onlySet = 1
        if (scope) params.scope = scope

        const { nodes, total } = await fetchTree(state.companyId, params)
        const keys = nodes.map((node) => node.key)

        commit(
            "upsertNodes",
            append
                ? nodes.map((node) =>
                      !node.match && state.nodes[node.key]?.match && previous.includes(node.key)
                          ? { ...node, match: true }
                          : node,
                  )
                : nodes,
        )
        commit("setFiltered", {
            keys: append ? [...previous, ...keys.filter((key) => !previous.includes(key))] : keys,
            total,
        })
    },
    async loadMore({ commit, dispatch, state }) {
        commit("setLoadingMore", true)

        try {
            await dispatch(isFilterActive(state.filter) ? "fetchFiltered" : "fetchRoots", true)
        } catch (e) {
            console.error("Error while loading more alarm notifications", e)
        } finally {
            commit("setLoadingMore", false)
        }
    },
    async fetchFocus({ commit, dispatch, state, rootGetters }) {
        const focusKey = state.focusKey ?? ""
        const [scope, id] = focusKey.split(":") as [AlarmNotificationScope, string]
        const name = await fetchFocusName(scope, id, rootGetters)
        const { nodes } = name
            ? await fetchTree(state.companyId, { search: name, scope, limit: MAX_LIMIT })
            : { nodes: [] as AlarmNotificationNode[] }
        const byKey = Object.fromEntries(nodes.map((node) => [node.key, node]))
        const chain: AlarmNotificationNode[] = []

        for (let node = byKey[focusKey]; node; node = byKey[node.parentKey ?? ""]) {
            chain.unshift(node)
        }

        if (!chain.length) {
            commit("setFocusKey", null)
            await dispatch("fetchRoots")
            return
        }

        commit("upsertNodes", chain)
        commit("setFiltered", { keys: chain.map((node) => node.key), total: 1 })

        if (!byKey[focusKey].hasChildren) return

        if (!state.expandedKeys.includes(focusKey)) {
            commit("setExpandedKeys", [...state.expandedKeys, focusKey])
        }

        await dispatch("fetchChildren", { key: focusKey, append: false })
    },
    async fetchChildren({ commit, state }, { key, append }: { key: string; append: boolean }) {
        const loaded = state.children[key]?.keys.length ?? 0

        commit("addLoadingKey", key)

        try {
            const { nodes, total } = await fetchTree(state.companyId, {
                parent: key,
                offset: append ? loaded : 0,
                limit: append
                    ? CHILDREN_PAGE_SIZE
                    : Math.min(Math.max(loaded, CHILDREN_PAGE_SIZE), MAX_LIMIT),
            })

            commit("upsertNodes", nodes)
            commit("setChildren", {
                key,
                page: { keys: nodes.map((node) => node.key), total },
                append,
            })
        } finally {
            commit("removeLoadingKey", key)
        }
    },
    async toggleNode({ commit, dispatch, state }, key: string) {
        if (state.expandedKeys.includes(key)) {
            commit(
                "setExpandedKeys",
                state.expandedKeys.filter((k) => k !== key),
            )
            return
        }

        commit("setExpandedKeys", [...state.expandedKeys, key])

        if (!state.children[key]) {
            try {
                await dispatch("fetchChildren", { key, append: false })
            } catch (e) {
                console.error("Error while fetching alarm notification children", e)
                commit(
                    "setExpandedKeys",
                    state.expandedKeys.filter((k) => k !== key),
                )
            }
        }
    },
    async applyFilter({ commit, dispatch }, filter: AlarmNotificationFilter) {
        commit("setFilter", filter)
        commit("setFocusKey", null)
        commit("setFiltered", null)
        commit("setExpandedKeys", [])
        await dispatch("loadPage")
    },
    async refresh({ dispatch, state }) {
        const expanded = state.expandedKeys.filter((key) => state.children[key])

        await Promise.all([
            dispatch("loadPage"),
            ...expanded
                .filter((key) => key !== state.focusKey)
                .map((key) => dispatch("fetchChildren", { key, append: false })),
        ])
    },
    async saveNotification(
        { commit, dispatch },
        { scope, id, own, state: target }: AlarmNotificationChange,
    ): Promise<boolean> {
        const key = nodeKey(scope, id)

        if (nodeState(own) === target) return false

        commit("addSavingKey", key)

        try {
            if (target === "DEFAULT" && own) {
                await axios.delete(`${ENDPOINT}/${own.subscriptionId}`)
            } else if (own) {
                await axios.patch(`${ENDPOINT}/${own.subscriptionId}`, { data: { type: target } })
            } else {
                await axios.post(ENDPOINT, {
                    data: { scope, [scopeIdField[scope]]: id, type: target },
                })
            }

            return true
        } catch (e) {
            console.error("Error while saving alarm notification", e)
            dispatch(
                "toastMessageModule/showMessage",
                { message: "alarm_notifications.save_error", color: "red", isTranslation: true },
                { root: true },
            )

            return false
        } finally {
            commit("removeSavingKey", key)
        }
    },
    async fetchEffective({ commit }, { scope, id }: AlarmNotificationTarget) {
        try {
            const res = await axios.get<{ data: AlarmNotificationEffective }>(
                `${ENDPOINT}/effective`,
                { params: { [scopeIdField[scope]]: id } },
            )

            commit("setEffective", res.data.data)
        } catch (e) {
            console.error("Error while fetching alarm notification state", e)
            commit("setEffective", null)
        }
    },
    loadDelivery({ commit, rootGetters }) {
        const user = rootGetters["auth/user"] ?? {}

        commit("setDelivery", {
            alarmLevels: user.alarmLevels ?? [],
            alarmEmailOptIn: !!user.alarmEmailOptIn,
            alarmOkEmailOptIn: !!user.alarmOkEmailOptIn,
            alarmPushNotificationOptIn: !!user.alarmPushNotificationOptIn,
            alarmOkPushNotificationOptIn: !!user.alarmOkPushNotificationOptIn,
        })
    },
    async saveDelivery(
        { commit, dispatch, state },
        delivery: AlarmNotificationDelivery,
    ): Promise<boolean> {
        const previous = state.delivery

        commit("setDelivery", delivery)
        commit("setSavingDelivery", true)

        try {
            const res = await axios.get("/auth/user")

            await axios.post("/auth/user", { data: { ...res.data.data.user, ...delivery } })

            return true
        } catch (e) {
            console.error("Error while saving alarm delivery settings", e)
            commit("setDelivery", previous)
            dispatch(
                "toastMessageModule/showMessage",
                { message: "alarm_notifications.save_error", color: "red", isTranslation: true },
                { root: true },
            )

            return false
        } finally {
            commit("setSavingDelivery", false)
        }
    },
}

const getters: GetterTree<AlarmNotificationsState, RootState> = {
    nodes: ({ nodes }) => nodes,
    children: ({ children }) => children,
    expandedKeys: ({ expandedKeys }) => expandedKeys,
    filter: ({ filter }) => filter,
    filtered: ({ filtered }) => filtered,
    focusKey: ({ focusKey }) => focusKey,
    companyId: ({ companyId }) => companyId,
    loadingMore: ({ loadingMore }) => loadingMore,
    filteredMatches: ({ filtered, nodes }) => matchedCount(filtered?.keys ?? [], nodes),
    loading: ({ loading }) => loading,
    loadingKeys: ({ loadingKeys }) => loadingKeys,
    savingKeys: ({ savingKeys }) => savingKeys,
    delivery: ({ delivery }) => delivery,
    savingDelivery: ({ savingDelivery }) => savingDelivery,
    effective: ({ effective }) => effective,
    isFilterActive: ({ filter }) => isFilterActive(filter),
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<AlarmNotificationsState, RootState>
